/**
 * AuthContext - Global authentication state management
 * Provides consistent user information across all components
 */

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { authService } from '../services/AuthService';
import type { User } from '../types/api';

interface AuthContextType {
    isAuthenticated: boolean;
    hasFullAccess: boolean;
    user: User | null;
    userName: string;
    isLoading: boolean;
    isEnterprise: boolean;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Global cache to prevent multiple simultaneous auth checks
let authCheckPromise: Promise<void> | null = null;

interface AuthProviderProps {
    children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [hasFullAccess, setHasFullAccess] = useState(false);
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const checkAuth = async () => {
        try {

            authCheckPromise = (async () => {
                // First check if token exists before making API call
                if (!authService.isAuthenticated()) {
                    setIsAuthenticated(false);
                    setHasFullAccess(false);
                    setUser(null);
                    setIsLoading(false);
                    return;
                }

                // Check full access using the same endpoint as RootRoute
                const fullAccessCheck = await authService.checkAccessState();

                if (fullAccessCheck.isAuthenticated) {
                    // Get user data if authenticated (only force refresh on first call)
                    const currentUser = await authService.getCurrentUser(false);

                    if (currentUser.success && currentUser.data) {
                        setIsAuthenticated(true);
                        setUser(currentUser.data);
                        setHasFullAccess(fullAccessCheck.hasFullAccess);
                    } else {
                        setIsAuthenticated(false);
                        setHasFullAccess(false);
                        setUser(null);
                    }
                } else {
                    setIsAuthenticated(false);
                    setHasFullAccess(false);
                    setUser(null);
                }
            })();

            await authCheckPromise;
        } catch (error) {
            console.error('Auth check failed:', error);
            setIsAuthenticated(false);
            setHasFullAccess(false);
            setUser(null);
            // Clear invalid token if auth check failed
            authService.clearAllAuthData();
        } finally {
            setIsLoading(false);
            authCheckPromise = null; // Clear promise after completion
        }
    };

    useEffect(() => {
        // Initial check
        checkAuth();

        // Listen for authentication state changes
        const handleAuthChange = () => {
            // Clear cache to force fresh check
            authCheckPromise = null;
            checkAuth();
        };

        // Listen for custom events and storage changes
        window.addEventListener('authStateChanged', handleAuthChange);
        window.addEventListener('storage', handleAuthChange);

        return () => {
            window.removeEventListener('authStateChanged', handleAuthChange);
            window.removeEventListener('storage', handleAuthChange);
        };
    }, []);

    const logout = async () => {
        try {
            // Immediately clear local state to prevent race conditions
            setIsAuthenticated(false);
            setHasFullAccess(false);
            setUser(null);

            // Clear global auth cache to prevent stale data
            authCheckPromise = null;

            // Then perform logout API call
            await authService.logout();
            // Navigate will be handled by the component calling this
        } catch (error) {
            console.error('Logout failed:', error);
            // Even if API fails, ensure local state is cleared
            setIsAuthenticated(false);
            setHasFullAccess(false);
            setUser(null);

            // Clear global auth cache even on error
            authCheckPromise = null;
        }
    };

    // Helper function to determine if user is enterprise
    // Return false while loading to prevent undefined values, but ensure components wait for loading to complete
    const isEnterprise = Boolean(user?.user_type === 'enterprise');


    const value: AuthContextType = {
        isAuthenticated,
        hasFullAccess,
        user,
        userName: user?.name?.split(' ')[0] || '',
        isLoading,
        isEnterprise,
        logout,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
